import {
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  disarmFocusHandoff,
  focusNavRow,
  focusTargetAfterRemoval,
  isFocusHandoffArmed,
  NAV_CONTAINERS,
  planFocusHandoff,
  setPendingHandoffKey,
  takePendingHandoffKey,
} from "./focus-handoff";

/** Сколько ждать, пока панель вернёт фокус сама, прежде чем перенести его на соседа. */
const DEFERRED_HANDOFF_MS = 600;

/** Фокус ни на чём: браузер уронил его на `body` вместе с удалённой строкой. */
function isFocusLost(): boolean {
  const active = document.activeElement;
  return active === null || active === document.body;
}

import { sameStringList, useStableValue } from "./use-stable";

/**
 * Роверный `tabindex` для списка строк, ходящих стрелками, и его шаг —
 * общий хелпер для таблицы плотностей и списка ситуаций §9/этап 5. Строка
 * не любой таблицы: список ситуаций вперемешку держит ключи групп и ключи
 * участников, поэтому идентичность строки — произвольная строка-ключ из
 * `keys`, а не номер (сортировка и поток переставляют строки под курсором) и
 * не `density_id` (у строки группы его вовсе нет).
 */
export interface RowNavigation {
  /**
   * Эта строка сейчас единственная точка табуляции списка. Ссылка на функцию
   * постоянна, ответ читается из последнего рендера: вызывать надо во время
   * рендера родителя и передавать строке готовое `boolean`.
   */
  focusable: (key: string) => boolean;
  /** Строка получила фокус — запомнить её ключ как текущий. */
  onFocus: (key: string) => void;
  /**
   * Обработчик `onKeyDown` строки с данным ключом; ссылка постоянна, ключ
   * передаётся аргументом, а не замыканием: мемоизированной строке нужен
   * обработчик, не меняющийся от рендера к рендеру. Индекс строки берётся
   * из внутренней карты `ключ → индекс`, а не поиском по массиву на каждый
   * вызов: список ситуаций держит вперемешку сотни ключей групп и участников,
   * и поиск `indexOf` на каждую строку рендера был бы квадратичным (design.md,
   * ревью пункт 14). Шаг — ArrowDown/ArrowUp; `preventDefault` вызывается
   * только тогда, когда шаг действительно возможен, иначе браузерная
   * прокрутка стрелками не должна гаситься зря.
   */
  onKeyDown: (key: string, event: ReactKeyboardEvent<HTMLElement>) => void;
  /**
   * Индекс строки — точки табуляции в `keys` (`activeRowIndex`). Виртуальная
   * таблица рисует её и её соседей всегда, даже вне окна прокрутки (design
   * stage9-table-virtual Д4).
   */
  activeIndex: number;
}

/**
 * Способ найти соседнюю строку по шагу навигации (design.md change
 * stage6-density-cards, решение 5):
 * - `"dom"` — сосед в DOM (`parentElement.children`), как в `SituationList`:
 *   строки там — прямые соседи в разметке;
 * - `"keys"` — сосед в плоском списке ключей, найденный по `data-row-key`
 *   внутри ближайшего контейнера навигации (`NAV_CONTAINERS`): у карточек
 *   между последней строкой одной карточки и первой строкой следующей нет
 *   общего родителя в DOM, а в виртуальной таблице между нарисованными
 *   строками стоят распорки (design stage9-table-virtual Д5).
 */
export type RowNavigationStep = "dom" | "keys";

export interface RowNavigationOptions {
  step?: RowNavigationStep;
}

/**
 * Соседний ключ по шагу `step` (`1` — вперёд, `-1` — назад) в плоском списке
 * `keys`. Чистая часть шага `"keys"`, тестируемая без DOM — тем же приёмом,
 * что и `activeRowIndex`. Индекс берётся из карты `ключ → индекс`, а не
 * поиском по массиву: список — сотни ключей карточек и строк, и `indexOf`
 * на каждое нажатие стрелки был бы квадратичным (design.md, ревью пункт 14).
 * Неизвестный ключ и выход за края списка — `null`.
 */
export function nextRowKey(
  indexByKey: ReadonlyMap<string, number>,
  keys: readonly string[],
  key: string,
  step: number,
): string | null {
  const index = indexByKey.get(key);
  if (index === undefined) {
    return null;
  }
  const next = index + step;
  if (next < 0 || next >= keys.length) {
    return null;
  }
  return keys[next] ?? null;
}

/**
 * Индекс единственной точки табуляции по текущему списку ключей и последнему
 * сфокусированному ключу — чистая часть хука, тестируемая без React: `useState`
 * не поддаётся модульному тесту напрямую в этом проекте (нет
 * jsdom/testing-library, только `bun:test` и `renderToStaticMarkup`).
 * Ключ, которого в текущем списке больше нет (строка исчезла из-под курсора —
 * ушла в терминал, отсеяна отбором), откатывает точку на первую строку, а не
 * теряет её вовсе.
 */
export function activeRowIndex(keys: readonly string[], focusedKey: string | null): number {
  const focusedIndex = focusedKey === null ? -1 : keys.indexOf(focusedKey);
  return focusedIndex >= 0 ? focusedIndex : 0;
}

export { focusTargetAfterRemoval };

export function useRowNavigation(
  rawKeys: readonly string[],
  options: RowNavigationOptions = {},
): RowNavigation {
  const step = options.step ?? "dom";
  // Вызывающие строят массив ключей заново на каждый рендер (`rows.map(...)`):
  // по содержимому он почти не меняется, и ссылка держится прежней, чтобы
  // карта индексов и эффект переноса фокуса не работали впустую.
  const keys = useStableValue(rawKeys, sameStringList);
  const [focusedKey, setFocusedKey] = useState<string | null>(null);
  const indexByKey = useMemo(() => {
    const map = new Map<string, number>();
    keys.forEach((key, index) => {
      map.set(key, index);
    });
    return map;
  }, [keys]);
  const activeIndex = activeRowIndex(keys, focusedKey);

  // Строка с фокусом ушла с экрана из-за скрытия монеты (§75 п. 2): браузер
  // роняет фокус на `body`, и клавиатурная работа по списку обрывалась бы.
  // Переход взводится только самим действием скрытия (`armFocusHandoff`) и
  // срабатывает один раз; уход строки по другой причине — терминальный
  // переход, отбор — фокус не двигает (design.md Д8). Эффект зависит только от
  // состава ключей, а последний сфокусированный ключ читает из ref.
  const focusedKeyRef = useRef(focusedKey);
  focusedKeyRef.current = focusedKey;
  const previousKeysRef = useRef(keys);
  useEffect(() => {
    const previousKeys = previousKeysRef.current;
    previousKeysRef.current = keys;
    const plan = planFocusHandoff({
      armed: isFocusHandoffArmed(),
      focusedKey: focusedKeyRef.current,
      previousKeys,
      keys,
      dialogOpen: document.querySelector('[role="dialog"]') !== null,
    });
    if (plan.kind === "idle") {
      return;
    }
    if (plan.kind === "drop") {
      disarmFocusHandoff();
      return;
    }
    if (plan.kind === "defer") {
      // Панель закроется и вернёт фокус сама — туда, где его взять (`finalFocus`).
      // Если не вернёт, цель выбирается по таймеру, а взвод не висит вечно.
      setPendingHandoffKey(plan.key);
      window.setTimeout(() => {
        const key = takePendingHandoffKey();
        if (key !== null && isFocusLost()) {
          focusNavRow(key);
        }
      }, DEFERRED_HANDOFF_MS);
      return;
    }
    // Фокус, ушедший в поле или панель, не отнимается.
    if (isFocusLost()) {
      focusNavRow(plan.key);
    }
    disarmFocusHandoff();
  }, [keys]);

  // Функции ниже имеют постоянную ссылку и читают актуальные ключи и индекс
  // из ref, обновляемого на каждый рендер: строки обёрнуты в `memo`, и новая
  // функция на рендер перерисовывала бы все строки списка.
  const latest = useRef({ keys, indexByKey, activeIndex, step });
  latest.current = { keys, indexByKey, activeIndex, step };

  const focusable = useCallback(
    (key: string) => latest.current.indexByKey.get(key) === latest.current.activeIndex,
    [],
  );
  const onFocus = useCallback((key: string) => setFocusedKey(key), []);
  const onKeyDown = useCallback((key: string, event: ReactKeyboardEvent<HTMLElement>) => {
    const { keys: currentKeys, indexByKey: currentIndex, step: currentStep } = latest.current;
    const arrowStep = event.key === "ArrowDown" ? 1 : event.key === "ArrowUp" ? -1 : 0;
    if (arrowStep === 0) {
      return;
    }

    if (currentStep === "keys") {
      const next = nextRowKey(currentIndex, currentKeys, key, arrowStep);
      if (next === null) {
        return;
      }
      event.preventDefault();
      // Между последней строкой одной карточки и первой строкой следующей
      // нет общего родителя в DOM — соседа ищем по ключу внутри ближайшего
      // контейнера навигации, а не по `parentElement.children`.
      const container = event.currentTarget.closest(NAV_CONTAINERS);
      const target = container?.querySelector(`[data-row-key="${CSS.escape(next)}"]`);
      if (target instanceof HTMLElement) {
        target.focus();
      }
      return;
    }

    const index = currentIndex.get(key);
    if (index === undefined) {
      return;
    }
    const next = index + arrowStep;
    if (next < 0 || next >= currentKeys.length) {
      return;
    }
    event.preventDefault();
    const target = event.currentTarget.parentElement?.children[next];
    if (target instanceof HTMLElement) {
      target.focus();
    }
  }, []);

  return useMemo(
    () => ({ focusable, onFocus, onKeyDown, activeIndex }),
    [focusable, onFocus, onKeyDown, activeIndex],
  );
}
